import z from 'zod';

import { leaveBreadcrumb } from 'cfa-react-logging';
import { statusCodes } from './customerErrorMessages';

export class MappedError extends Error {
  constructor(message: string, type: string) {
    super(message);
    this.name = type;
  }
}

export async function parseAsJson(response: Response) {
  await throwIfNotOk(response);

  if (!isJsonResponse(response)) {
    leaveBreadcrumb('Received a response that is not JSON', { response });
    throwDefaultError();
  }

  try {
    const json = await response.json();
    return json;
  } catch (e: unknown) {
    leaveBreadcrumb('Failed to parse response as JSON', { response, error: e });
    throwDefaultError();
  }
}

export function handleFetchFail(error: Error): never {
  leaveBreadcrumb('Fetch Failure', { error });
  return throwDefaultError();
}

function isJsonResponse(response: Response) {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.startsWith('application/json');
}

const throwDefaultError = () => {
  throw new MappedError("We couldn't complete your request right now. Please try again.", 'Error');
};

export const throwIfNotOk = async (response: Response) => {
  if (!response.ok) {
    leaveBreadcrumb('Response is not ok', { response });

    if (isJsonResponse(response)) {
      const json = await response.json();
      const { data } = z
        .object({ statusCode: z.number().optional(), statusMessage: z.string().optional() })
        .safeParse(json);
      if (data) {
        const { statusMessage, statusCode } = data;
        if (statusCode && statusCodes[statusCode as keyof typeof statusCodes]) {
          const mapped = statusCodes[statusCode as keyof typeof statusCodes];
          throw new MappedError(mapped.message, mapped.type);
        } else if (statusMessage) {
          throw new MappedError(statusMessage, 'Error');
        }
      }
    }

    return throwDefaultError();
  }
};
